import React, { useState, useEffect } from "react";
import { 
  Lock, 
  KeyRound,   
  Save, 
  Loader2,
  AlertTriangle,
  FileSignature, 
  Activity,       
  DollarSign,    
  CheckCircle
} from "lucide-react";

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";

// ==============================================================
// COMPONENTE AUXILIAR PARA INPUTS DE API (Simplificado e Limpo)
// ==============================================================
const ApiKeyInput = ({ label, value, onChange, placeholder, id }) => (
  <div className="flex flex-col w-full mb-1">
    <label htmlFor={id} className="block text-xs font-bold text-light-text-secondary dark:text-dark-text-secondary uppercase mb-2 ml-1">
      {label}
    </label>
    <input 
      id={id}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      // 🟢 MUTAÇÃO MOBILE: Adicionado break-all para garantir que os tokens não vazem
      className="w-full px-4 py-3.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-sm focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary transition-colors text-light-text-primary dark:text-dark-text-primary shadow-inner font-mono break-all" 
    />
  </div>
);

const AdminIntegracoesPage = () => {
  const { userInfo } = useAuth();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showStatus, setShowStatus] = useState(null); 
  
  const [lastUpdateInfo, setLastUpdateInfo] = useState({ name: "Desconhecido", date: null });

  const [fcProdToken, setFcProdToken] = useState("");
  const [clickSignProdToken, setClickSignProdToken] = useState("");
  const [clickSignTemplateId, setClickSignTemplateId] = useState("");
  const [asaasProdToken, setAsaasProdToken] = useState("");
  const [asaasTestToken, setAsaasTestToken] = useState("");

  const brandColor = "#00a99d"; // 🟢 Cor da marca adicionada para o ícone

  useEffect(() => {
    const fetchApiConfigs = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "settings", "api_integrations");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setFcProdToken(data.fc_prod_token || "");
          setClickSignProdToken(data.clicksign_prod_token || "");
          setClickSignTemplateId(data.clicksign_template_id || "");
          setAsaasProdToken(data.asaas_prod_token || "");
          setAsaasTestToken(data.asaas_test_token || "");

          setLastUpdateInfo({
             name: data.updatedByName || "Desconhecido",
             date: data.updatedAt ? data.updatedAt.toDate().toLocaleDateString('pt-BR') : "Sem data"
          });
        }
      } catch (error) {
        console.error("Erro ao carregar configurações do cofre:", error);
        setShowStatus({ type: 'error', message: 'Falha ao acessar o cofre de chaves. Verifique as permissões de SuperAdmin.' });
      } finally {
        setLoading(false);
      }
    };

    fetchApiConfigs();
  }, []);

  const handleSaveConfigs = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setShowStatus(null);

    if (!fcProdToken || !clickSignProdToken || !clickSignTemplateId || !asaasProdToken) {
        if (!window.confirm("Atenção: Algumas chaves de API essenciais estão vazias. Isso pode bloquear os serviços na plataforma. Deseja salvar assim mesmo?")) {
            setIsSaving(false);
            return;
        }
    }

    try {
      const docRef = doc(db, "settings", "api_integrations");
      
      const payload = {
        fc_prod_token: fcProdToken,
        clicksign_prod_token: clickSignProdToken,
        clicksign_template_id: clickSignTemplateId,
        asaas_prod_token: asaasProdToken,
        asaas_test_token: asaasTestToken,
        updatedAt: serverTimestamp(),
        updatedByName: userInfo?.name || "SuperAdmin",
        updatedByUid: userInfo?.uid || "uid_desconhecido"
      };

      await setDoc(docRef, payload, { merge: true });
      
      setShowStatus({ type: 'success', message: 'Cofre de chaves atualizado com sucesso! As APIs reais estão prontas.' });
      setLastUpdateInfo({ name: userInfo?.name || "SuperAdmin", date: new Date().toLocaleDateString('pt-BR') });

    } catch (error) {
      console.error("Erro ao salvar configurações do cofre:", error);
      setShowStatus({ type: 'error', message: 'Falha ao salvar as chaves no cofre. Verifique suas permissões.' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setShowStatus(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in bg-light-bg dark:bg-dark-bg">
        <Loader2 className="w-12 h-12 text-brand-primary animate-spin mb-4" />
        <p className="text-light-text-secondary font-medium">Abrindo Cofre de Segurança...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in bg-light-bg dark:bg-dark-bg min-h-full font-sans overflow-x-hidden relative pb-24 lg:pb-8">
      
      {/* CABEÇALHO: PADRÃO REAL BENEFÍCIOS */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-light-border dark:border-dark-border pb-6">
        <div className="flex items-center gap-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center shrink-0 border border-teal-100/50 dark:border-teal-800/30">
                <Lock className="w-6 h-6 md:w-7 md:h-7" style={{ color: brandColor }} />
            </div>
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                    Cofre de Integrações de API
                </h1>
                <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">
                    Gestão restrita e segura das chaves de API para os serviços de terceiros (Módulo 2). Estas chaves permitem a conexão real com a FC Análises, ClickSign e Asaas.
                </p>
            </div>
        </div>
      </div>

      {/* FEEDBACK DE STATUS (Sucesso/Erro) */}
      {showStatus && (
        <div className={`p-4 rounded-xl border flex items-center gap-3 animate-fade-in shadow-sm ${showStatus.type === 'success' ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/10 dark:border-green-800' : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/10 dark:border-red-800'}`}>
          {showStatus.type === 'success' ? <CheckCircle className="w-5 h-5 shrink-0"/> : <AlertTriangle className="w-5 h-5 shrink-0"/>}
          <p className="text-sm font-medium">{showStatus.message}</p>
        </div>
      )}

      {/* FORMULÁRIO PRINCIPAL */}
      <form onSubmit={handleSaveConfigs} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* COLUNA ESQUERDA: Configurações Gerais e Auditoria */}
        <div className="col-span-1 space-y-6">
            <div className="bg-light-card dark:bg-dark-card rounded-2xl border border-light-border dark:border-dark-border shadow-sm p-6 flex flex-col items-center text-center">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
                <h2 className="text-lg font-bold text-light-text-primary dark:text-dark-text-primary">Blindagem de Segurança</h2>
                <p className="text-xs text-light-text-secondary mt-2 leading-relaxed">
                    As chaves de API gravadas aqui são armazenadas de forma segura e criptografada no Google Firestore. Apenas usuários com a flag <strong className="font-mono text-slate-500 break-all">superadmin: true</strong> nas regras de segurança possuem acesso de leitura e escrita neste documento. Não compartilhe estas chaves com ninguém.
                </p>
            </div>

            <div className="bg-light-card dark:bg-dark-card rounded-2xl border border-light-border dark:border-dark-border shadow-sm p-6 space-y-4">
                <h3 className="text-sm font-bold text-brand-primary flex items-center gap-2 border-b border-light-border dark:border-dark-border pb-3">
                    <KeyRound className="w-4 h-4"/> Histórico de Atualização
                </h3>
                <div className="space-y-3 text-xs text-light-text-secondary">
                    <div className="flex flex-col">
                        <span className="uppercase tracking-wider opacity-70 mb-0.5">Última alteração por:</span>
                        <strong className="text-light-text-primary dark:text-dark-text-primary text-sm">{lastUpdateInfo.name}</strong>
                    </div>
                    <div className="flex flex-col">
                        <span className="uppercase tracking-wider opacity-70 mb-0.5">Data da Gravação:</span>
                        <strong className="text-light-text-primary dark:text-dark-text-primary text-sm">{lastUpdateInfo.date}</strong>
                    </div>
                </div>
            </div>
        </div>

        {/* COLUNA DIREITA: Os Inputs das Chaves */}
        <div className="lg:col-span-2 space-y-6">
            
            {/* CARD 1: FC ANÁLISES */}
            <div className="bg-light-card dark:bg-dark-card rounded-2xl border-t-4 border-t-blue-500 border-y border-r border-y-light-border border-r-light-border dark:border-y-dark-border dark:border-r-dark-border shadow-sm p-5 sm:p-6">
                <h2 className="text-lg font-bold text-light-text-primary dark:text-dark-text-primary flex items-center gap-2 mb-6 border-b border-light-border dark:border-dark-border pb-4">
                    <Activity className="w-5 h-5 text-blue-500 shrink-0"/> <span className="truncate">FC Análises</span>
                </h2>
                <div className="space-y-4">
                    <ApiKeyInput 
                        label="Token de API (Produção)" 
                        id="fcProdToken"
                        value={fcProdToken}
                        onChange={setFcProdToken}
                        placeholder="Insira o Token Real da FC..."
                    />
                    <p className="text-[11px] text-light-text-secondary leading-relaxed bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                        Este token habilita o envio real dos PDFs de CNH, RG e Comprovantes para a API da FC Análises realizar o OCR Antifraude e retornar a análise de risco sintética.
                    </p>
                </div>
            </div>

            {/* CARD 2: CLICKSIGN */}
            <div className="bg-light-card dark:bg-dark-card rounded-2xl border-t-4 border-t-emerald-500 border-y border-r border-y-light-border border-r-light-border dark:border-y-dark-border dark:border-r-dark-border shadow-sm p-5 sm:p-6">
                <h2 className="text-lg font-bold text-light-text-primary dark:text-dark-text-primary flex items-center gap-2 mb-6 border-b border-light-border dark:border-dark-border pb-4">
                    <FileSignature className="w-5 h-5 text-emerald-500 shrink-0"/> <span className="truncate">ClickSign</span>
                </h2>
                <div className="space-y-6">
                    <ApiKeyInput 
                        label="API Access Token (Produção)" 
                        id="clickSignProdToken"
                        value={clickSignProdToken}
                        onChange={setClickSignProdToken}
                        placeholder="Insira o Token de API da ClickSign..."
                    />
                    <div className="flex flex-col w-full">
                        <label htmlFor="clickSignTemplateId" className="block text-xs font-bold text-light-text-secondary dark:text-dark-text-secondary uppercase mb-2 ml-1">
                            Template ID (Contrato Mestre)
                        </label>
                        <input 
                            id="clickSignTemplateId"
                            type="text" 
                            value={clickSignTemplateId}
                            onChange={(e) => setClickSignTemplateId(e.target.value)}
                            placeholder="ex: b4f1a2c3-d5e6-7f8a-9b0c-d1e2f3a4b5c6"
                            className="w-full pl-4 pr-4 py-3.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-sm focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary transition-colors text-light-text-primary dark:text-dark-text-primary shadow-inner font-mono break-all"
                        />
                         <p className="text-[11px] text-light-text-secondary leading-relaxed mt-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                            Este ID é gerado lá no painel da ClickSign após o upload do arquivo `.docx` do contrato mestre de Fiança Locatícia.
                        </p>
                    </div>
                </div>
            </div>

            {/* CARD 3: ASAAS */}
            <div className="bg-light-card dark:bg-dark-card rounded-2xl border-t-4 border-t-orange-500 border-y border-r border-y-light-border border-r-light-border dark:border-y-dark-border dark:border-r-dark-border shadow-sm p-5 sm:p-6 relative overflow-hidden">
                <h2 className="text-lg font-bold text-light-text-primary dark:text-dark-text-primary flex items-center gap-2 mb-6 border-b border-light-border dark:border-dark-border pb-4 pr-24">
                    <DollarSign className="w-5 h-5 text-orange-500 shrink-0"/> <span className="truncate">Asaas</span>
                </h2>
                
                {/* SELO DE PENDÊNCIA (Ajustado para mobile) */}
                <span className="absolute right-4 sm:right-6 top-4 sm:top-6 flex items-center gap-1.5 px-3 py-1 text-[10px] font-black uppercase bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-full border border-orange-200 dark:border-orange-800 tracking-wider">
                    <AlertTriangle className="w-3.5 h-3.5 hidden sm:block"/> Congelado
                </span>

                <div className="space-y-6 opacity-60">
                    <ApiKeyInput 
                        label="Token de API (Produção)" 
                        id="asaasProdToken"
                        value={asaasProdToken}
                        onChange={setAsaasProdToken}
                        placeholder="Em breve..."
                    />
                    <ApiKeyInput 
                        label="Token de API (Homologação/Testes)" 
                        id="asaasTestToken"
                        value={asaasTestToken}
                        onChange={setAsaasTestToken}
                        placeholder="Em breve..."
                    />
                </div>
            </div>

            {/* 🟢 BARRA DE AÇÕES INFERIOR (Sticky no Mobile para Fat Fingers) */}
            <div className="fixed bottom-0 left-0 right-0 p-4 lg:relative lg:p-0 z-50 lg:z-auto bg-light-bg/90 dark:bg-dark-bg/90 backdrop-blur-md lg:bg-transparent border-t border-light-border dark:border-dark-border lg:border-none shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] lg:shadow-none">
              <div className="max-w-7xl mx-auto flex justify-center lg:justify-end lg:bg-light-card lg:dark:bg-dark-card lg:rounded-2xl lg:border lg:border-light-border lg:dark:border-dark-border lg:shadow-sm lg:p-4">
                  <button 
                    type="submit" 
                    disabled={isSaving || loading}
                    className="flex items-center justify-center gap-2.5 px-8 py-4 lg:py-3 w-full lg:w-auto bg-brand-primary text-white rounded-xl lg:rounded-full font-black text-sm hover:bg-teal-600 transition-colors shadow-md disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5"/>}
                    {isSaving ? "Gravando Chaves..." : "Gravar Chaves Imediatamente"}
                  </button>
              </div>
            </div>

        </div>
      </form>

    </div>
  );
};

export default AdminIntegracoesPage;